import {
	Background,
	BackgroundVariant,
	Controls,
	ReactFlow,
	useNodesInitialized,
	useNodesState,
	useReactFlow,
	type NodeMouseHandler,
} from "@xyflow/react";
import React, { useCallback, useEffect, useMemo } from "react";

import type { ContextGraph, ContextNode } from "../../types/contextGraph";
import ContextGraphEdge, {
	CONTEXT_MARKER_ID,
	PATH_MARKER_ID,
} from "./ContextGraphEdge";
import ContextGraphNode from "./ContextGraphNode";
import { CardRectsContext } from "./cardRects";
import { layoutContextGraph, type ContextFlowNode } from "./layout";

/**
 * Registered outside the component: xyflow warns (and re-mounts every node) if
 * these object identities change between renders.
 */
const NODE_TYPES = { contextNode: ContextGraphNode };
const EDGE_TYPES = { contextEdge: ContextGraphEdge };

/**
 * Fit tightly and let the graph reach 1:1.
 *
 * The small padding is deliberate: this graph is wider than it is tall, so
 * fitView is bounded by the width and generous padding just buys empty canvas.
 * `maxZoom` is above 1 so a small graph — a message-scoped neighbourhood of
 * four or five cards — grows to fill the canvas instead of sitting marooned in
 * the middle at its natural size.
 */
export const FIT_VIEW_OPTIONS = { padding: 0.06, maxZoom: 1.25 } as const;

/**
 * Re-fits once xyflow has measured the cards.
 *
 * `fitView` on the first paint fits nothing: the nodes are still unmeasured, so
 * the viewport is computed against boxes that do not exist yet. And expanding a
 * "+N" card adds nodes that are unmeasured all over again, which is why this
 * re-arms on every change to the set of nodes rather than only on mount.
 *
 * It deliberately does NOT re-arm when a node merely moves: re-fitting under a
 * reader who has just dragged a card would snatch the canvas back from them.
 *
 * Lives inside `<ReactFlow>` because the hooks need its store.
 */
const FitViewOnLayout: React.FC<{ signature: string }> = ({ signature }) => {
	// Read, not gated on: `useNodesInitialized` only reports true once EVERY node
	// has handle bounds, which this graph never quite reaches, so gating the
	// re-fit on it meant it never ran. It is still a useful trigger — it changes
	// as measurement progresses — so the fit is re-armed when it does.
	const initialized = useNodesInitialized();
	const { fitView } = useReactFlow();
	useEffect(() => {
		// Deferred, not immediate: expanding a "+N" card adds nodes xyflow has not
		// measured yet, and fitting against their absent sizes leaves the graph
		// clipped. A timer rather than `requestAnimationFrame` because rAF is
		// throttled in a headless browser, which is where this gets screenshotted.
		const timer = setTimeout(() => void fitView(FIT_VIEW_OPTIONS), 80);
		return () => clearTimeout(timer);
	}, [initialized, fitView, signature]);
	return null;
};

export interface ContextGraphCanvasProps {
	graph: ContextGraph;
	/** Overrides the graph's own focus; `undefined` keeps it. */
	focus?: string | null;
	onSelectNode: (node: ContextNode) => void;
}

/**
 * The canvas: dagre-positioned cards, curved labelled edges, pan/zoom.
 *
 * The minimap is off by design — the graph is one company's neighbourhood, a
 * few dozen cards, and a minimap of that is a thumbnail of the thing already on
 * screen.
 *
 * Nodes are held in xyflow's own state via `useNodesState` and re-seeded from
 * the layout whenever the graph or the focus changes. That is what makes them
 * DRAGGABLE: with `nodes` passed as a plain prop and no `onNodesChange`,
 * xyflow computes each drag's position changes and then discards them
 * (`triggerNodeChanges` applies nothing unless the flow owns the nodes or the
 * caller handles the changes), so a dragged card snapped straight back.
 */
const ContextGraphCanvas: React.FC<ContextGraphCanvasProps> = ({
	graph,
	focus,
	onSelectNode,
}) => {
	const laidOut = useMemo(
		() => layoutContextGraph(graph, focus === undefined ? graph.focus : focus),
		[graph, focus],
	);

	const [nodes, setNodes, onNodesChange] = useNodesState<ContextFlowNode>(
		laidOut.nodes,
	);

	// Re-seed when the graph or the focus changes — a new question is a new
	// layout, and any dragging the reader did belonged to the old one.
	useEffect(() => setNodes(laidOut.nodes), [laidOut, setNodes]);

	// Refit when the set of cards changes, not when one of them moves.
	const signature = useMemo(
		() => laidOut.nodes.map((node) => node.id).join("|"),
		[laidOut],
	);

	// Where every card currently is, recomputed from the LIVE node state so a
	// label knows what it must dodge even mid-drag. `ContextGraphEdge` reads
	// this; passing it through context keeps it one computation for the canvas
	// rather than one per edge.
	const cardRects = useMemo(
		() =>
			nodes.map((node) => ({
				left: node.position.x,
				right: node.position.x + node.data.size.width,
				top: node.position.y,
				bottom: node.position.y + node.data.size.height,
			})),
		[nodes],
	);

	const handleNodeClick = useCallback<NodeMouseHandler<ContextFlowNode>>(
		(_event, node) => onSelectNode(node.data.node),
		[onSelectNode],
	);

	return (
		<CardRectsContext.Provider value={cardRects}>
		<div className="context-graph h-full w-full" data-testid="context-graph-canvas">
			<svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
				<defs>
					{/* One marker per stroke colour: SVG markers cannot inherit the
					    stroke of the path that references them. */}
					<marker
						id={PATH_MARKER_ID}
						markerWidth="10"
						markerHeight="10"
						refX="8"
						refY="3.5"
						orient="auto"
						markerUnits="strokeWidth"
					>
						<path d="M0,0 L7,3.5 L0,7 z" fill="var(--cg-path)" />
					</marker>
					<marker
						id={CONTEXT_MARKER_ID}
						markerWidth="12"
						markerHeight="12"
						refX="9"
						refY="4"
						orient="auto"
						markerUnits="strokeWidth"
					>
						<path d="M0,0 L8,4 L0,8 z" fill="var(--cg-edge)" />
					</marker>
				</defs>
			</svg>

			<ReactFlow
				nodes={nodes}
				edges={laidOut.edges}
				onNodesChange={onNodesChange}
				nodeTypes={NODE_TYPES}
				edgeTypes={EDGE_TYPES}
				onNodeClick={handleNodeClick}
				fitView
				fitViewOptions={FIT_VIEW_OPTIONS}
				nodesDraggable
				nodesConnectable={false}
				elementsSelectable
				proOptions={{ hideAttribution: true }}
				minZoom={0.2}
				maxZoom={1.75}
			>
				<FitViewOnLayout signature={signature} />
				{/* Dots, not a grid: a ruled grid behind a diagram of boxes is what
				    makes the whole thing read as a flow chart. */}
				<Background
					variant={BackgroundVariant.Dots}
					color="var(--cg-grid)"
					gap={28}
					size={1.4}
				/>
				<Controls showInteractive={false} />
			</ReactFlow>
		</div>
		</CardRectsContext.Provider>
	);
};

export default ContextGraphCanvas;
